import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Button } from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import { Alert, Loading } from '@vnetwork/ui/feedback';
import { AuthShell } from './components/AuthShell';
import { PasswordInput } from './components/AuthShell';
import { resetPassword, validateResetToken } from '@/api/auth-flow';

interface FormValues {
  new_password: string;
  new_password_confirmation: string;
}

export default function ResetPasswordPage() {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const tokenCheck = useQuery({
    queryKey: ['auth', 'reset-token', token],
    queryFn: () => validateResetToken(token),
    enabled: Boolean(token),
    retry: false,
  });

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<FormValues>({
    defaultValues: { new_password: '', new_password_confirmation: '' },
  });

  const { mutateAsync, isPending } = useMutation({ mutationFn: resetPassword });

  const onSubmit = async (values: FormValues) => {
    setError(null);
    try {
      const res = await mutateAsync({ token, ...values });
      if (res.errors?.length) {
        setError(res.errors[0].message);
        return;
      }
      setDone(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not reset password.');
    }
  };

  const invalid =
    !token || tokenCheck.isError || tokenCheck.data?.valid === false;

  let body: React.ReactNode;
  if (token && tokenCheck.isLoading) {
    body = <Loading />;
  } else if (done) {
    body = (
      <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
        <Alert tone="success" title="Password updated">
          You can now sign in with your new password.
        </Alert>
        <Button asChild variant="primary" fullWidth>
          <Link to="/auth/login">Continue to sign in</Link>
        </Button>
      </div>
    );
  } else if (invalid) {
    body = (
      <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
        <Alert tone="danger" title="Invalid or expired link">
          This reset link is no longer valid. Request a new one.
        </Alert>
        <Button asChild variant="secondary" fullWidth>
          <Link to="/auth/forgot-password">Request a new link</Link>
        </Button>
      </div>
    );
  } else {
    body = (
      <form
        onSubmit={handleSubmit(onSubmit)}
        style={{ display: 'grid', gap: 'var(--vn-space-4)' }}
      >
        {error ? <Alert tone="danger" title={error} /> : null}
        <Field label="New password" required error={errors.new_password?.message}>
          {(p) => (
            <PasswordInput
              {...p}
              autoComplete="new-password"
              invalid={!!errors.new_password}
              {...register('new_password', {
                required: 'Password is required.',
                minLength: { value: 9, message: 'Password must be 9–64 characters.' },
                maxLength: { value: 64, message: 'Password must be 9–64 characters.' },
              })}
            />
          )}
        </Field>
        <Field
          label="Confirm new password"
          required
          error={errors.new_password_confirmation?.message}
        >
          {(p) => (
            <PasswordInput
              {...p}
              autoComplete="new-password"
              invalid={!!errors.new_password_confirmation}
              {...register('new_password_confirmation', {
                required: 'Please confirm your password.',
                validate: (v) =>
                  v === watch('new_password') || 'Passwords do not match.',
              })}
            />
          )}
        </Field>
        <Button type="submit" variant="primary" fullWidth loading={isPending}>
          Set new password
        </Button>
      </form>
    );
  }

  return (
    <AuthShell
      title="Set a new password"
      subtitle="Choose a strong password you don't use elsewhere."
    >
      {body}
    </AuthShell>
  );
}
